import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle, Lightning, ShieldCheck, ChatCircle, Users, Lightbulb } from "@phosphor-icons/react";

export default function Landing() {
  return (
    <div className="min-h-screen bg-black text-zinc-50 overflow-x-hidden">
      {/* Header */}
      <header
        data-testid="landing-header"
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/70 border-b border-white/5"
      >
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 bg-yellow-400 rounded-lg flex items-center justify-center">
              <Lightbulb weight="fill" size={20} className="text-black" />
            </div>
            <span className="font-heading font-black text-xl tracking-tight">Mastra Media</span>
            <span className="font-mono text-xs text-zinc-500 hidden sm:inline">LLP</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="header-login-link">
              <Button variant="ghost" className="text-zinc-300 hover:text-white hover:bg-zinc-900">Sign in</Button>
            </Link>
            <Link to="/register" data-testid="header-register-link">
              <Button className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-full px-5">Get Started</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-40 pb-24 bg-grid">
        <div className="absolute inset-x-0 top-0 h-[600px] bg-gradient-to-b from-yellow-400/5 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 relative">
          <div className="grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-yellow-400/10 border border-yellow-400/20 text-yellow-400 text-xs font-mono mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-yellow-400 animate-pulse" />
                Trusted by 15,000+ creators
              </div>
              <h1 className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight mb-6">
                Ship influencer<br />
                campaigns without <span className="text-yellow-400">chaos.</span>
              </h1>
              <p className="text-zinc-400 text-lg max-w-xl mb-8 leading-relaxed">
                The command center where Mastra Media, creators, and brands align.
                Upload, review, feedback, approve — all in one clean workflow.
              </p>
              <div className="flex flex-wrap items-center gap-4">
                <Link to="/register" data-testid="hero-cta-primary">
                  <Button className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold text-base rounded-full px-8 py-6 h-auto">
                    Start free →
                  </Button>
                </Link>
                <Link to="/login" data-testid="hero-cta-secondary">
                  <Button variant="outline" className="border-white/10 bg-transparent hover:bg-white/5 text-zinc-100 rounded-full px-8 py-6 h-auto">
                    Sign in
                  </Button>
                </Link>
              </div>
              <div className="mt-10 flex items-center gap-8 text-sm text-zinc-500">
                <div className="flex items-center gap-2"><CheckCircle weight="fill" size={16} className="text-yellow-400" /> Multi-role dashboards</div>
                <div className="flex items-center gap-2 hidden sm:flex"><CheckCircle weight="fill" size={16} className="text-yellow-400" /> Real-time feedback</div>
              </div>
            </div>

            <div className="md:col-span-5 relative">
              <div className="relative rounded-2xl overflow-hidden border border-white/10 glow-gold">
                <img
                  src="https://images.pexels.com/photos/12433032/pexels-photo-12433032.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Creator with ring light"
                  className="w-full h-[520px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/40 to-transparent" />
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="backdrop-blur-xl bg-zinc-900/70 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono text-xs text-zinc-400">SUBMISSION #4821</span>
                      <span className="px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 text-xs font-medium">Approved</span>
                    </div>
                    <p className="text-sm font-medium">Summer Drop Campaign · @creator_name</p>
                  </div>
                </div>
              </div>
              <div className="absolute -top-4 -left-6 w-32 h-32 bg-yellow-400/20 blur-3xl rounded-full pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      {/* Features Bento */}
      <section className="py-24 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16 max-w-2xl">
            <span className="font-mono text-xs text-yellow-400 uppercase tracking-widest">// Built for scale</span>
            <h2 className="font-heading font-black text-4xl sm:text-5xl mt-3 leading-tight">
              Everything an agency needs.<br />Nothing it doesn't.
            </h2>
          </div>

          <div className="grid md:grid-cols-6 gap-4 auto-rows-[220px]">
            <FeatureCard
              className="md:col-span-4"
              icon={<Users size={28} weight="duotone" />}
              title="Three roles, one workflow"
              desc="Agency admins orchestrate. Influencers submit. Brands approve. Clean permissions from day one."
              accent
            />
            <FeatureCard
              className="md:col-span-2"
              icon={<Lightning size={28} weight="duotone" />}
              title="Lightning-fast reviews"
              desc="Approve or reject in one click with structured feedback loops."
            />
            <FeatureCard
              className="md:col-span-2"
              icon={<ChatCircle size={28} weight="duotone" />}
              title="Feedback that ships"
              desc="Threaded revision history. Nothing gets lost."
            />
            <FeatureCard
              className="md:col-span-4"
              icon={<ShieldCheck size={28} weight="duotone" />}
              title="Content lifecycle you can trust"
              desc="Every submission has a status: Pending, Approved, Rejected, or Revised. Full audit trail — every action timestamped."
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading font-black text-4xl sm:text-5xl mb-4">
            Ready to run cleaner campaigns?
          </h2>
          <p className="text-zinc-400 text-lg mb-8">Join Mastra Media in bringing order to influencer marketing.</p>
          <Link to="/register" data-testid="footer-cta">
            <Button className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold text-base rounded-full px-10 py-6 h-auto animate-pulse-glow">
              Create your workspace →
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-white/5 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <span className="font-mono text-xs text-zinc-500">© 2026 Mastra Media LLP</span>
          <span className="text-xs text-zinc-500">Made for creators, brands, and the agencies that move them.</span>
        </div>
      </footer>
    </div>
  );
}

function FeatureCard({ icon, title, desc, className = "", accent = false }) {
  return (
    <div
      className={`relative rounded-2xl border p-6 flex flex-col justify-between overflow-hidden card-hover ${
        accent ? "bg-yellow-400 text-black border-yellow-400" : "bg-zinc-900/60 border-white/10 text-zinc-100"
      } ${className}`}
    >
      <div>{icon}</div>
      <div>
        <h3 className="font-heading font-bold text-2xl leading-tight mb-2">{title}</h3>
        <p className={`text-sm ${accent ? "text-zinc-800" : "text-zinc-400"}`}>{desc}</p>
      </div>
    </div>
  );
}
